import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { showNotification } from '@mantine/notifications'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { notifyTicketsWorkspaceStale } from '@/lib/projects-events'
import type { TicketDetailTicket } from '@/types/api'

type UseTicketManagementActionsParams = {
  ticket: TicketDetailTicket | null
  ticketId: string | null
  setTicket: Dispatch<SetStateAction<TicketDetailTicket | null>>
}

export type PendingStatusPayload = {
  reason: string
  comment?: string
}

export function useTicketManagementActions({
  ticket,
  ticketId,
  setTicket,
}: UseTicketManagementActionsParams) {
  const onStatus = useCallback(
    async (status: string, extra?: Record<string, unknown>) => {
      if (!ticketId) return
      try {
        const saved = await api.ticketUpdate(ticketId, { status, ...(extra || {}) })
        setTicket((prev) => (prev?.id === saved.id ? { ...prev, status: saved.status } : prev))
        notifyTicketsWorkspaceStale()
        showNotification({ title: 'Статус', message: 'Сохранён.', color: 'green' })
      } catch (requestError) {
        showNotification({
          title: 'Ошибка',
          message: requestError instanceof ApiError ? requestError.message : 'Не удалось сменить статус',
          color: 'red',
        })
      }
    },
    [setTicket, ticketId],
  )

  const onPriority = useCallback(
    async (priority: string) => {
      if (!ticketId) return
      try {
        const saved = await api.ticketUpdate(ticketId, { priority })
        setTicket((prev) => (prev?.id === saved.id ? { ...prev, priority: saved.priority } : prev))
        notifyTicketsWorkspaceStale()
        showNotification({ title: 'Приоритет', message: 'Сохранён.', color: 'green' })
      } catch (requestError) {
        showNotification({
          title: 'Ошибка',
          message: requestError instanceof ApiError ? requestError.message : 'Не удалось сменить приоритет',
          color: 'red',
        })
      }
    },
    [setTicket, ticketId],
  )

  const onAssign = useCallback(
    async (assignedToId: string | null) => {
      if (!ticketId) return
      try {
        const saved = await api.ticketUpdate(ticketId, { assigned_to_id: assignedToId })
        setTicket((prev) => (prev?.id === saved.id ? { ...prev, assigned_to_id: saved.assigned_to_id } : prev))
        notifyTicketsWorkspaceStale()
        showNotification({ title: 'Назначение', message: 'Сохранено.', color: 'green' })
      } catch (requestError) {
        showNotification({
          title: 'Ошибка',
          message: requestError instanceof ApiError ? requestError.message : 'Не удалось назначить',
          color: 'red',
        })
      }
    },
    [setTicket, ticketId],
  )

  const onAddTag = useCallback(
    async (tagId: string) => {
      if (!ticketId || !ticket) return
      const next = [...new Set([...(ticket.tag_ids || []), tagId])]
      try {
        await api.ticketUpdate(ticketId, { tag_ids: next })
        setTicket((prev) => (prev ? { ...prev, tag_ids: next } : prev))
        notifyTicketsWorkspaceStale()
      } catch (requestError) {
        showNotification({
          title: 'Ошибка',
          message: requestError instanceof ApiError ? requestError.message : 'Не удалось добавить метку',
          color: 'red',
        })
      }
    },
    [setTicket, ticket, ticketId],
  )

  const onRemoveTag = useCallback(
    async (tagId: string) => {
      if (!ticketId || !ticket) return
      const next = (ticket.tag_ids || []).filter((id) => id !== tagId)
      try {
        await api.ticketUpdate(ticketId, { tag_ids: next })
        setTicket((prev) => (prev ? { ...prev, tag_ids: next } : prev))
        notifyTicketsWorkspaceStale()
      } catch (requestError) {
        showNotification({
          title: 'Ошибка',
          message: requestError instanceof ApiError ? requestError.message : 'Не удалось удалить метку',
          color: 'red',
        })
      }
    },
    [setTicket, ticket, ticketId],
  )

  const onPending = useCallback(async (payload: PendingStatusPayload) => {
    await onStatus('pending', {
      pending_reason: payload.reason,
      pending_comment: payload.comment || '',
    })
  }, [onStatus])

  const changeReplyLocale = useCallback(
    (next: 'en' | 'tr' | null) => {
      if (!ticketId) return
      void api
        .patchTicketTranslationReplyLocale(ticketId, next)
        .then((response) => {
          setTicket((currentTicket) =>
            currentTicket
              ? {
                  ...currentTicket,
                  translation_reply_locale_override: response.translation_reply_locale_override ?? null,
                  translation_client_locale:
                    response.translation_client_locale ?? currentTicket.translation_client_locale,
                }
              : currentTicket,
          )
          showNotification({
            title: 'Язык ответов',
            message: next
              ? `Следующие ответы клиенту — на ${next === 'tr' ? 'турецком' : 'английском'}.`
              : 'Снова используется язык по первому сообщению клиента.',
            color: 'green',
          })
        })
        .catch((requestError) => {
          showNotification({
            title: 'Ошибка',
            message: requestError instanceof ApiError ? requestError.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
            color: 'red',
          })
        })
    },
    [setTicket, ticketId],
  )

  return {
    changeReplyLocale,
    onAddTag,
    onAssign,
    onPending,
    onPriority,
    onRemoveTag,
    onStatus,
  }
}
